import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DotsThreeVerticalIcon } from '@phosphor-icons/react';
import type { UserCompanyGroupDto } from '@monorepo/shared';
import { GroupsTableActionMenu } from './GroupsTableActionMenu';
import { BulkActionsBar } from './BulkActionsBar';

interface GroupsTableProps {
  groups: UserCompanyGroupDto[];
  onGroupAction: (groupId: string, action: 'edit' | 'delete' | 'view') => void;
  onBulkDelete?: (groupIds: string[]) => void;
  isCurrentUserAdmin: boolean;
}

export const GroupsTable = ({
  groups,
  onGroupAction,
  onBulkDelete,
  isCurrentUserAdmin,
}: GroupsTableProps) => {
  const { t } = useTranslation();
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuId) {
        const target = event.target as HTMLElement;
        if (!target.closest('.action-menu') && !target.closest('.action-button')) {
          setOpenMenuId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const handleMenuToggle = (groupId: string) => {
    if (openMenuId === groupId) {
      setOpenMenuId(null);
    } else {
      const button = buttonRefs.current[groupId];
      if (button) {
        const rect = button.getBoundingClientRect();
        setMenuPosition({
          top: rect.bottom + 8,
          right: window.innerWidth - rect.right,
        });
      }
      setOpenMenuId(groupId);
    }
  };

  const toggleSelectAll = () => {
    if (selectedGroups.size === groups.length) {
      setSelectedGroups(new Set());
    } else {
      setSelectedGroups(new Set(groups.map((g) => g.id)));
    }
  };

  const toggleSelectGroup = (groupId: string) => {
    const newSelected = new Set(selectedGroups);
    if (newSelected.has(groupId)) {
      newSelected.delete(groupId);
    } else {
      newSelected.add(groupId);
    }
    setSelectedGroups(newSelected);
  };

  const handleBulkDelete = () => {
    onBulkDelete?.(Array.from(selectedGroups));
    setSelectedGroups(new Set());
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('fr-FR');
  };

  return (
    <div className="flex flex-col w-full">
      {selectedGroups.size > 0 && (
        <BulkActionsBar
          count={selectedGroups.size}
          actions={
            isCurrentUserAdmin && onBulkDelete
              ? [
                  {
                    key: 'delete',
                    label: t('admin.groups.actions.delete'),
                    onClick: handleBulkDelete,
                    variant: 'danger',
                  },
                ]
              : []
          }
        />
      )}

      <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="w-full border-separate border-spacing-y-3">
          <thead className="bg-[#f4f5f6] sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2.5 text-left rounded-l-md">
                <input
                  type="checkbox"
                  checked={selectedGroups.size === groups.length && groups.length > 0}
                  onChange={toggleSelectAll}
                  className="w-5 h-5 text-primary bg-white border-gray-300 rounded focus:ring-primary"
                />
              </th>
              <th className="px-3 py-2.5 text-left text-sm font-medium text-[#5c6670]">
                {t('admin.groups.table.columns.name')}
              </th>
              <th className="px-3 py-2.5 text-left text-sm font-medium text-[#5c6670] hidden md:table-cell">
                {t('admin.groups.table.columns.member_count')}
              </th>
              <th className="px-3 py-2.5 text-left text-sm font-medium text-[#5c6670] hidden xl:table-cell">
                {t('admin.groups.table.columns.created_at')}
              </th>
              <th className="px-3 py-2.5 text-right rounded-r-md"></th>
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                  {t('admin.groups.table.empty')}
                </td>
              </tr>
            ) : (
              groups.map((group) => (
                <tr
                  key={group.id}
                  className="bg-white rounded-md shadow-sm border-b border-[#dee7ed] hover:bg-gray-50 transition"
                >
                  <td className="px-3 py-4 rounded-l-md">
                    <input
                      type="checkbox"
                      checked={selectedGroups.has(group.id)}
                      onChange={() => toggleSelectGroup(group.id)}
                      className="w-5 h-5 text-primary bg-white border-gray-300 rounded focus:ring-primary"
                    />
                  </td>
                  <td className="w-full max-w-0 px-3 py-4">
                    <div className="flex min-w-0 items-center">
                      <div className="flex-shrink-0 w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-medium">
                        {group.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="ml-4 min-w-0">
                        <div className="truncate text-sm font-medium text-[#171a1c]">
                          {group.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {t('admin.groups.table.members_count', { count: group.memberCount })}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500 hidden md:table-cell">
                    {group.memberCount}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500 hidden xl:table-cell">
                    {formatDate(group.createdAt)}
                  </td>
                  <td className="px-3 py-2 text-right rounded-r-md">
                    <button
                      ref={(el) => (buttonRefs.current[group.id] = el)}
                      onClick={() => handleMenuToggle(group.id)}
                      className="action-button bg-white border border-[#becfda] p-2 rounded-md shadow-sm hover:bg-gray-50 transition"
                    >
                      <DotsThreeVerticalIcon size={20} weight="bold" className="text-[#171a1c]" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {openMenuId && (
        <GroupsTableActionMenu
          groupId={openMenuId}
          position={menuPosition}
          isCurrentUserAdmin={isCurrentUserAdmin}
          onAction={onGroupAction}
          onClose={() => setOpenMenuId(null)}
        />
      )}
    </div>
  );
};
